import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Modulo from "../../components/funcionarios/Modulo";
import ListaDeEspera from "../../components/funcionarios/ListaDeEspera";
import {
  finalizarAtencion,
  descartarAtencion,
  getHomeFuncionario,
  llamarEstudianteParticular,
  obtenerMeson,
  obtenerFuncionario,
  getMesones,
} from "../../services/apis_funcionarios";
import { Container, Row, Col } from "react-bootstrap";

const styles = {
  container: {
    fontFamily: "var(--font-funcionarios)",
  },
};

function Atencion() {
  const navigate = useNavigate();
  const [cola, setCola] = useState([]);
  const [actual, setActual] = useState(null);
  const [siguiente, setSiguiente] = useState(null);
  const [modulo, setModulo] = useState({ hasFuncionario: false });
  const [funcionarioId, setFuncionarioId] = useState(null);
  const [loadingFuncionario, setLoadingFuncionario] = useState(true);

  // Obtener información del funcionario desde /api/me/
  useEffect(() => {
    const API_URL = (import.meta?.env?.VITE_API_URL || "").replace(/\/$/, "");
    const url = `${API_URL}/api/me/`;

    fetch(url, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`API /me/ respondió con ${res.status}`);
        return res.json();
      })
      .then((data) => {

        const funcionarioId = data?.funcionario?.id;
        if (funcionarioId) {
          setFuncionarioId(funcionarioId);
        } else {
          console.warn("El usuario no tiene un funcionario asociado");
        }
      })
      .catch((err) => {
        console.error("Error obteniendo datos del usuario:", err);
      })
      .finally(() => {
        setLoadingFuncionario(false);
      });
  }, []);

  const getHome = async () => {
    if (!funcionarioId) return; // Esperar a que se cargue el funcionarioId

    try {
      const data = await getHomeFuncionario(funcionarioId);
      const nuevaCola = Array.isArray(data.cola) ? data.cola : [];

      setActual({
        solicitante: data.usuario_en_atencion || null,
        atencion: data.atencion || null,
        motivo: data.motivo_en_atencion,
        numeroTicket: data.atencion?.numero_ticket || null,
      });

      // Obtener el funcionario para saber qué mesón tiene asignado
      const funcionarioData = await obtenerFuncionario(funcionarioId);

      if (funcionarioData.meson) {
        // Obtener la lista completa de mesones y buscar el asignado
        const mesones = await getMesones();
        const mesonAsignado = mesones.find(
          (m) => m.id === funcionarioData.meson
        );

        if (mesonAsignado) {
          setModulo({
            hasFuncionario: true,
            estado: mesonAsignado.estado,
            nombre: mesonAsignado.nombre,
            motivos: mesonAsignado.motivos,
          });

          // Filtrar la cola por los motivos del mesón asignado
          const colaFiltrada = nuevaCola.filter((item) =>
            mesonAsignado.motivos?.some(
              (m) => Number(m.id) === Number(item.motivo.id)
            )
          );

          setCola(colaFiltrada);
          setSiguiente(colaFiltrada[0] || null);
        } else {
          setModulo({ hasFuncionario: false });
          setCola([]);
          setSiguiente(null);
        }
      } else {
        setModulo({ hasFuncionario: false });
        setCola([]);
        setSiguiente(null);
      }
    } catch (error) {
      console.error("Error cargando la cola:", error);
      setModulo({ hasFuncionario: false });
      setCola([]);
      setSiguiente(null);
    }
  };

  useEffect(() => {
    if (!funcionarioId) return; // Esperar a que se cargue

    getHome();
    const interval = setInterval(getHome, 5000);
    return () => clearInterval(interval);
  }, [funcionarioId]);

  const handleDescartar = async () => {

    if (!actual.solicitante) {
      console.warn("No hay atención activa para descartar.");
      return;
    }

    try {
      await descartarAtencion(actual.atencion.id);
      await getHome(); // refresca la cola
    } catch (err) {
      console.error("Error descartando atención:", err);
    }
  };

  const handleFinalizar = async () => {

    if (!actual.solicitante) {
      console.warn("No hay atención activa para finalizar.");
      return;
    }

    try {
      await finalizarAtencion(actual.atencion.id);
      await getHome(); // refresca la cola
    } catch (err) {
      console.error("Error finalizando atención:", err);
    }
  };

  // Maneja el botón "Atender" de un estudiante particular en la ListaDeEspera a partir del id del ticket
  const handleAtenderEstudiante = async (idT, idS) => {
    try {
      const data = await llamarEstudianteParticular(idT, funcionarioId);
      navigate(`/atencion/ficha`, { state: { idActual: idS } });
    } catch (err) {
      console.error("Error atendiendo estudiante particular:", err);
    }
  };

  if (loadingFuncionario) {
    return (
      <Container fluid className="p-0 m-0 w-100">
        <Row
          className="justify-content-center align-items-center"
          style={{ minHeight: "60dvh" }}
        >
          <Col xs="auto" className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-muted">
              Cargando información del funcionario...
            </p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0 m-0 w-100">
      {!modulo.hasFuncionario ? (
        <Row
          className="justify-content-center align-items-center"
          style={{ minHeight: "60dvh" }}
        >
          <Col xs="auto" className="text-center text-muted">
            <h5>No estás atendiendo ningún módulo aún.</h5>
          </Col>
        </Row>
      ) : (
        <Row className="g-3">
          <Col xs={12}>
            <Modulo
              nombre={modulo.nombre}
              estado={modulo.estado}
              siguiente={siguiente}
              actual={actual}
              onAtender={handleAtenderEstudiante}
              onFinalizar={handleFinalizar}
              onDescartar={handleDescartar}
            />
          </Col>
          <Col xs={12}>
            <ListaDeEspera
              cola={cola.slice(1)} // Excluir al siguiente que ya se muestra en Modulo
              onAtender={handleAtenderEstudiante}
              actual={actual}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default Atencion;
